import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useBusinessUnitApi custom hook for business unit API operations.
 * - Provides CRUD operations for business units using Apollo Client and GraphQL.
 * - Handles cache updates for business unit queries and mutations.
 * - Exposes helpers for reading/writing/updating business units in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting business units.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 */

// GraphQL Queries and Mutations
const BUSINESS_UNIT_QUERIES = {
  // Query to fetch all business units
  GET_BUSINESS_UNITS: gql`
    query GetBusinessUnits {
      GetBusinessUnits {
        business_unit_id
        business_unit_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Query to fetch a business unit by ID
  GET_BUSINESS_UNIT_BY_ID: gql`
    query GetBusinessUnitById($businessUnitId: ID!) {
      GetBusinessUnitById(business_unit_id: $businessUnitId) {
        business_unit_id
        business_unit_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to create a new business unit
  ADD_BUSINESS_UNIT: gql`
    mutation CreateBusinessUnit($businessUnitName: String!) {
      CreateBusinessUnit(business_unit_name: $businessUnitName) {
        business_unit_id
        business_unit_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to update an existing business unit
  UPDATE_BUSINESS_UNIT: gql`
    mutation UpdateBusinessUnit(
      $businessUnitId: ID!
      $businessUnitName: String
      $isActive: Boolean
    ) {
      UpdateBusinessUnit(
        business_unit_id: $businessUnitId
        business_unit_name: $businessUnitName
        is_active: $isActive
      ) {
        business_unit_id
        business_unit_name
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to delete a business unit
  DELETE_BUSINESS_UNIT: gql`
    mutation DeleteBusinessUnit($businessUnitId: ID!) {
      DeleteBusinessUnit(business_unit_id: $businessUnitId) {
        business_unit_id
        business_unit_name
        is_active
        created_on
        updated_on
      }
    }
  `,
};

/**
 * Cache operations for business units.
 * - readBusinessUnits: Reads business units from Apollo cache.
 * - writeBusinessUnits: Writes business units to Apollo cache.
 * - updateBusinessUnitInCache: Updates a business unit in Apollo cache.
 */
const cacheOperations = {
  readBusinessUnits: () => {
    try {
      const { GetBusinessUnits = [] } =
        client.readQuery({
          query: BUSINESS_UNIT_QUERIES.GET_BUSINESS_UNITS,
        }) || {};
      return GetBusinessUnits;
    } catch {
      return [];
    }
  },
  writeBusinessUnits: (businessUnits) => {
    client.writeQuery({
      query: BUSINESS_UNIT_QUERIES.GET_BUSINESS_UNITS,
      data: { GetBusinessUnits: businessUnits },
    });
  },
  updateBusinessUnitInCache: (updatedBusinessUnit) => {
    const existingBusinessUnits = cacheOperations.readBusinessUnits();
    const updatedBusinessUnits = existingBusinessUnits.map((businessUnit) =>
      businessUnit.business_unit_id === updatedBusinessUnit.business_unit_id
        ? updatedBusinessUnit
        : businessUnit
    );
    cacheOperations.writeBusinessUnits(updatedBusinessUnits);
  },
};

/**
 * useBusinessUnitApi hook: Provides CRUD operations and cache helpers for business units.
 * - getBusinessUnits: Fetches all business units from backend and updates cache.
 * - getBusinessUnitById: Fetches a business unit by ID.
 * - createBusinessUnit: Creates a new business unit and updates cache.
 * - updateBusinessUnit: Updates an existing business unit and updates cache.
 * - deleteBusinessUnit: Deletes a business unit and updates cache.
 * - cacheOperations: Direct helpers for cache manipulation.
 * @returns {Object} CRUD methods and cache operations.
 */
export const useBusinessUnitApi = () => {
  // Mutation for adding a business unit
  const [addBusinessUnitMutation] = useMutation(
    BUSINESS_UNIT_QUERIES.ADD_BUSINESS_UNIT,
    {
      update(cache, { data: { CreateBusinessUnit } }) {
        try {
          const existingData = cache.readQuery({
            query: BUSINESS_UNIT_QUERIES.GET_BUSINESS_UNITS,
          });
          if (existingData) {
            cache.writeQuery({
              query: BUSINESS_UNIT_QUERIES.GET_BUSINESS_UNITS,
              data: {
                GetBusinessUnits: [
                  CreateBusinessUnit,
                  ...existingData.GetBusinessUnits,
                ],
              },
            });
          }
        } catch (error) {
          console.error(
            "Error updating cache after adding business unit:",
            error
          );
        }
      },
    }
  );

  // Mutation for updating a business unit
  const [updateBusinessUnitMutation] = useMutation(
    BUSINESS_UNIT_QUERIES.UPDATE_BUSINESS_UNIT,
    {
      update(cache, { data: { UpdateBusinessUnit } }) {
        try {
          const existingData = cache.readQuery({
            query: BUSINESS_UNIT_QUERIES.GET_BUSINESS_UNITS,
          });
          if (existingData) {
            const newData = existingData.GetBusinessUnits.map((businessUnit) =>
              businessUnit.business_unit_id ===
              UpdateBusinessUnit.business_unit_id
                ? UpdateBusinessUnit
                : businessUnit
            );
            cache.writeQuery({
              query: BUSINESS_UNIT_QUERIES.GET_BUSINESS_UNITS,
              data: { GetBusinessUnits: newData },
            });
          }
        } catch (error) {
          console.error(
            "Error updating cache after updating business unit:",
            error
          );
        }
      },
    }
  );

  // Mutation for deleting a business unit
  const [deleteBusinessUnitMutation] = useMutation(
    BUSINESS_UNIT_QUERIES.DELETE_BUSINESS_UNIT,
    {
      update(cache, { data: { DeleteBusinessUnit } }) {
        try {
          const existingData = cache.readQuery({
            query: BUSINESS_UNIT_QUERIES.GET_BUSINESS_UNITS,
          });
          if (existingData) {
            cache.writeQuery({
              query: BUSINESS_UNIT_QUERIES.GET_BUSINESS_UNITS,
              data: {
                GetBusinessUnits: existingData.GetBusinessUnits.filter(
                  (businessUnit) =>
                    businessUnit.business_unit_id !==
                    DeleteBusinessUnit.business_unit_id
                ),
              },
            });
          }
        } catch (error) {
          console.error(
            "Error updating cache after deleting business unit:",
            error
          );
        }
      },
    }
  );

  /**
   * Fetches all business units from the backend and updates the cache.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getBusinessUnits = async () => {
    try {
      console.log("Calling getBusinessUnits API...");
      const { data } = await client.query({
        query: BUSINESS_UNIT_QUERIES.GET_BUSINESS_UNITS,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });
      console.log("getBusinessUnits response:", data);

      if (!data || !data.GetBusinessUnits) {
        throw new Error("No data received from getBusinessUnits");
      }

      // Update cache
      client.writeQuery({
        query: BUSINESS_UNIT_QUERIES.GET_BUSINESS_UNITS,
        data: { GetBusinessUnits: data.GetBusinessUnits },
      });
      return { success: true, data: data.GetBusinessUnits };
    } catch (error) {
      console.error("Error in getBusinessUnits:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches a business unit by ID.
   * @param {string|number} business_unit_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getBusinessUnitById = async (business_unit_id) => {
    try {
      const { data } = await client.query({
        query: BUSINESS_UNIT_QUERIES.GET_BUSINESS_UNIT_BY_ID,
        variables: { businessUnitId: business_unit_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetBusinessUnitById };
    } catch (error) {
      console.error("Error fetching business unit:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new business unit.
   * @param {Object} formData - { business_unit_name: string }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createBusinessUnit = async (formData) => {
    try {
      const { data } = await addBusinessUnitMutation({
        variables: {
          businessUnitName: formData.business_unit_name,
        },
      });
      // Return the new business directly from mutation response
      return { success: true, data: data.CreateBusinessUnit };
    } catch (error) {
      console.error("Error creating business unit:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Updates an existing business unit.
   * @param {string|number} businessUnitId
   * @param {Object} formData - { business_unit_name: string }
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateBusinessUnit = async (businessUnitId, formData) => {
    try {
      const { data } = await updateBusinessUnitMutation({
        variables: {
          businessUnitId: businessUnitId,
          businessUnitName: formData.business_unit_name,
        },
      });
      // Return the updated business unit directly from mutation response
      return { success: true, data: data.UpdateBusinessUnit };
    } catch (error) {
      console.error("Error updating business unit:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Deletes a business unit by ID.
   * @param {string|number} business_unit_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteBusinessUnit = async (business_unit_id) => {
    try {
      const { data } = await deleteBusinessUnitMutation({
        variables: { businessUnitId: business_unit_id },
      });

      return { success: true, data: data.DeleteBusinessUnit };
    } catch (error) {
      console.error("Error deleting business unit:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getBusinessUnits,
    getBusinessUnitById,
    createBusinessUnit,
    updateBusinessUnit,
    deleteBusinessUnit,
    cacheOperations,
  };
};
